import { format, isValid, parse } from 'date-fns';
import React from 'react';

import { cn } from './cn';
import { FormField, type FormFieldProps } from './FormField';

const ISO_DATE = 'yyyy-MM-dd';

export interface DatePickerProps extends Omit<FormFieldProps, 'children'> {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  disabled?: boolean;
  min?: Date;
  max?: Date;
  name?: string;
  inputClassName?: string;
}

/**
 * Minimal date input built on the native `<input type="date">`, wrapped in `FormField`.
 * Emits/accepts a JS `Date` (date-only, local midnight); empty input yields `undefined`.
 */
export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      onBlur,
      disabled,
      min,
      max,
      name,
      inputClassName,
      label,
      description,
      errorMessage,
      successMessage,
      required,
      className,
      id,
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;

    return (
      <FormField
        label={label}
        description={description}
        errorMessage={errorMessage}
        successMessage={successMessage}
        required={required}
        className={className}
        id={inputId}
      >
        <input
          ref={ref}
          id={inputId}
          type="date"
          name={name}
          disabled={disabled}
          value={value && isValid(value) ? format(value, ISO_DATE) : ''}
          min={min && isValid(min) ? format(min, ISO_DATE) : undefined}
          max={max && isValid(max) ? format(max, ISO_DATE) : undefined}
          onBlur={onBlur}
          onChange={(e) => {
            const raw = e.target.value;
            if (!raw) {
              onChange?.(undefined);
              return;
            }
            const parsed = parse(raw, ISO_DATE, new Date());
            onChange?.(isValid(parsed) ? parsed : undefined);
          }}
          className={cn(
            'w-full rounded-md border bg-surface px-3 py-2 text-sm text-text-primary transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500',
            errorMessage ? 'border-danger-400 focus:ring-danger-500 focus:border-danger-500' : 'border-gray-300',
            disabled && 'bg-gray-50 text-gray-500 cursor-not-allowed',
            inputClassName
          )}
        />
      </FormField>
    );
  }
);
DatePicker.displayName = 'DatePicker';
